import { type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Layout primitives — the two column arrangements for dense screens
// (design/components/layouts.html). Each major region is a landmark; DOM order
// follows reading order regardless of visual columns. Only these two (plus a
// single column) are sanctioned — screens don't invent column layouts.

const collapse = {
  md: { row: 'md:flex-row', w: 'md:w-[var(--aside-w)]' },
  lg: { row: 'lg:flex-row', w: 'lg:w-[var(--aside-w)]' },
} as const

export interface TwoColumnLayoutProps {
  /** Main content (flex:1). */
  children: ReactNode
  /** Support panel, 260–280px. */
  aside: ReactNode
  asideWidth?: number
  /** Breakpoint at/above which the aside sits beside the content. */
  collapseAt?: 'md' | 'lg'
  className?: string
}

export function TwoColumnLayout({
  children,
  aside,
  asideWidth = 280,
  collapseAt = 'lg',
  className,
}: TwoColumnLayoutProps) {
  const c = collapse[collapseAt]
  return (
    <div
      className={cn('flex w-full flex-col gap-6', c.row, className)}
      style={{ '--aside-w': `${asideWidth}px` } as CSSProperties}
    >
      <main className="min-w-0 flex-1">{children}</main>
      <aside className={cn('w-full shrink-0', c.w)}>{aside}</aside>
    </div>
  )
}

export interface ThreeColumnLayoutProps {
  /** Conversation list (274px). */
  list: ReactNode
  /** Active thread (flex:1). */
  main: ReactNode
  /** Contact panel (248px). */
  contact: ReactNode
  listLabel?: string
  /** Which single column shows below md, where only one panel fits (inbox drives this). */
  mobileView?: 'list' | 'main' | 'contact'
  className?: string
}

// Responsive tiers:
//  · below md (phone): one panel at a time — `mobileView` picks which.
//  · md–lg (tablet): list + main side by side; the contact panel is hidden
//    (the inbox surfaces it in a Drawer) — see tablet-responsive.md §5.
//  · lg+ (desktop): all three columns, as the 21 mockup is drawn.
export function ThreeColumnLayout({
  list,
  main,
  contact,
  listLabel,
  mobileView = 'list',
  className,
}: ThreeColumnLayoutProps) {
  const { t } = useTranslation('components')
  const show = (which: ThreeColumnLayoutProps['mobileView']) =>
    mobileView === which ? 'flex' : 'hidden'
  return (
    <div className={cn('flex h-full min-h-0 w-full overflow-hidden', className)}>
      <nav
        aria-label={listLabel ?? t('ui.conversationList')}
        className={cn(
          'min-h-0 w-full shrink-0 flex-col border-r border-line bg-surface-2 md:flex md:w-[274px]',
          show('list'),
        )}
      >
        {list}
      </nav>
      <main className={cn('min-h-0 min-w-0 flex-1 flex-col md:flex', show('main'))}>{main}</main>
      <aside
        className={cn(
          'min-h-0 w-full shrink-0 flex-col border-l border-line md:hidden lg:flex lg:w-[248px]',
          show('contact'),
        )}
      >
        {contact}
      </aside>
    </div>
  )
}
